import React, { useRef } from 'react';
import { IonItem, IonLabel, IonButton, IonIcon } from '@ionic/react';
import { imageOutline, closeCircleOutline } from 'ionicons/icons';
import { fieldLabelStyle, fieldValueStyle } from '../read/styles';
import { visuallyHiddenInputStyle } from './constants';

interface EditableImageBlockProps {
  imageData: string | null;
  onSelectFile: (file: File | null) => void;
  onClear: () => void;
}

/**
 * Representative-image picker for the create/edit form — the middle body of
 * `TopicBasicsBlock`'s card, so its row always has the description under it
 * and always carries a separator.
 */
const EditableImageBlock: React.FC<EditableImageBlockProps> = ({
  imageData,
  onSelectFile,
  onClear,
}) => {
  const topicImageInputRef = useRef<HTMLInputElement>(null);

  return (
    <>
      {imageData && (
        <div style={{ padding: '8px 8px 12px' }}>
          <img
            src={imageData}
            alt=""
            data-testid="topic-image-preview"
            style={{
              display: 'block',
              width: '100%',
              maxHeight: '320px',
              objectFit: 'cover',
              borderRadius: '8px',
            }}
          />
        </div>
      )}
      <IonItem lines="inset">
        <IonLabel>
          <p className="topic-form-label" style={fieldLabelStyle}>
            Image
          </p>
          <h2 style={fieldValueStyle}>
            {imageData ? 'Representative image selected' : 'No image'}
          </h2>
        </IonLabel>
        <IonButton
          fill="clear"
          slot="end"
          onClick={() => topicImageInputRef.current?.click()}
          aria-label={imageData ? 'Replace Topic Image' : 'Add Topic Image'}
          data-testid="topic-image-picker-button"
        >
          <IonIcon icon={imageOutline} slot="icon-only" />
        </IonButton>
        {imageData && (
          <IonButton
            fill="clear"
            color="medium"
            slot="end"
            onClick={onClear}
            aria-label="Clear Topic Image"
            data-testid="topic-image-clear-button"
          >
            <IonIcon icon={closeCircleOutline} slot="icon-only" />
          </IonButton>
        )}
      </IonItem>
      <input
        ref={topicImageInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        data-testid="topic-image-input"
        onChange={e => {
          onSelectFile(e.target.files?.[0] ?? null);
          e.target.value = '';
        }}
        style={visuallyHiddenInputStyle}
      />
    </>
  );
};

export default EditableImageBlock;
